import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

/** Hold-to-dictate mic. Records in the browser, transcribes with Whisper, appends to the field. */
export default function DictateButton({ onText, context = "", label = "Speak", testId = "dictate" }) {
  const [state, setState] = useState("idle"); // idle | recording | sending
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined")
      return toast.error("Your browser can't record audio");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return toast.error("Microphone permission denied");
    }
    try {
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 2000) { setState("idle"); return toast.error("That was too short — speak for a moment longer"); }
        setState("sending");
        try {
          const fd = new FormData();
          fd.append("file", blob, mime === "audio/webm" ? "note.webm" : "note.mp4");
          if (context) fd.append("context", context);
          const r = await api.post("/stt/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          onText(r.data.text);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not transcribe that");
        } finally { setState("idle"); }
      };
      rec.start();
      recorderRef.current = rec;
      setState("recording");
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      toast.error("Could not start recording on this device");
    }
  };

  const busy = state === "sending";
  return (
    <button
      type="button"
      onClick={state === "recording" ? stop : start}
      disabled={busy}
      data-testid={testId}
      title="Dictate in Hindi or English"
      className={`inline-flex items-center gap-1.5 small-caps text-[10px] px-3 h-7 border transition ${
        state === "recording"
          ? "border-[#C04000] text-[#C04000] bg-[#C04000]/10 animate-pulse"
          : "border-white/10 text-neutral-400 hover:text-[#F5B041] hover:border-[#F5B041]/50"
      }`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" />
        : state === "recording" ? <Square size={11} /> : <Mic size={12} />}
      {busy ? "Transcribing…" : state === "recording" ? "Stop" : label}
    </button>
  );
}
